import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { Component, inject } from '@angular/core';

import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { MatOptionModule } from '@angular/material/core';

type ValueType = 'string' | 'number' | 'true' | 'false' | 'null' | undefined | null;

interface FormValue {
  valueType: ValueType;
  stringValue: string;
  numberValue: number | undefined | null;
}

const EMPTY_FORM_VALUE: FormValue = { valueType: null, stringValue: '', numberValue: null };

@Component({
  selector: 'app-object-scalar-control',
  templateUrl: './object-scalar-control.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    TranslateModule,
    MatOptionModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: ObjectScalarControlComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: ObjectScalarControlComponent,
      multi: true,
    },
  ],
})
export class ObjectScalarControlComponent implements ControlValueAccessor, Validator {
  private formBuilder = inject(FormBuilder);

  readonly valueTypes: ValueType[] = ['string', 'number', 'true', 'false'];

  form: FormGroup;

  disabled = false;

  private onChange = (value: any) => {};

  private onTouched = () => {};

  constructor() {
    this.form = this.buildForm();
    this.form.valueChanges.subscribe((value) => {
      if (this.form.valid) {
        this.onChange(jsonFromFormValue(value));
      } else {
        this.onChange(null);
      }
    });
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.form?.invalid) {
      return { invalid: true };
    } else return null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {
    this.form.setValue(formValueFromJson(obj));
  }

  trackByValueType(index: number, item: ValueType): string {
    return item || index.toString();
  }

  private buildForm(): FormGroup {
    const valueType = this.formBuilder.control(null, Validators.required);
    const stringValue = this.formBuilder.control({ value: '', disabled: true });
    const numberValue = this.formBuilder.control({ value: undefined, disabled: true }, [
      Validators.required,
      Validators.max(999999999999999),
    ]);
    valueType?.valueChanges?.subscribe((value: ValueType | null) => {
      switch (value) {
        case 'string':
          numberValue.disable();
          numberValue.setValue(null);
          stringValue.enable();
          break;
        case 'number':
          stringValue.disable();
          stringValue.setValue('');
          numberValue.enable();
          break;
        case 'true':
        case 'false':
        case 'null':
        default:
          stringValue.disable();
          stringValue.setValue('');
          numberValue.disable();
          numberValue.setValue(null);
          break;
      }
    });
    return this.formBuilder.group({ valueType, stringValue, numberValue });
  }
}

function jsonFromFormValue({ valueType, stringValue, numberValue }: FormValue): string | null {
  switch (valueType) {
    case 'true':
    case 'false':
    case 'null':
      return valueType;
    case 'number':
      return numberValue !== null && numberValue !== undefined ? JSON.stringify(numberValue) : null;
    case 'string':
      return JSON.stringify(stringValue);
  }
  return null;
}

function formValueFromJson(json: string): FormValue {
  if (json === null) {
    return { ...EMPTY_FORM_VALUE };
  }

  const parsed = JSON.parse(json);
  if (parsed === null) {
    return { ...EMPTY_FORM_VALUE, valueType: 'null' };
  } else if (parsed === true) {
    return { ...EMPTY_FORM_VALUE, valueType: 'true' };
  } else if (parsed === false) {
    return { ...EMPTY_FORM_VALUE, valueType: 'false' };
  } else if (typeof parsed === 'string') {
    return { ...EMPTY_FORM_VALUE, valueType: 'string', stringValue: parsed };
  } else if (typeof parsed === 'number') {
    return { ...EMPTY_FORM_VALUE, valueType: 'number', numberValue: parsed };
  }

  return { ...EMPTY_FORM_VALUE };
}
